import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import type { AdminSurveyStats, AdminResultItem, QuestionBreakdown } from '../types/admin'

const PAGE_W = 612 // letter width in points
const PAGE_H = 792 // letter height in points
const MARGIN = 54 // 0.75in
const CONTENT_W = PAGE_W - MARGIN * 2

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return sec > 0 ? `${min}m ${sec}s` : `${min}m`
}

function today(): string {
  return new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function drawAccentBar(pdf: jsPDF) {
  pdf.setFillColor(124, 58, 237)
  pdf.rect(0, 0, PAGE_W, 32, 'F')
}

function drawFooter(pdf: jsPDF, title: string, page: number) {
  pdf.setFontSize(8)
  pdf.setTextColor(100, 116, 139)
  pdf.text(title, MARGIN, PAGE_H - 28)
  pdf.text(`Page ${page}`, PAGE_W - MARGIN, PAGE_H - 28, { align: 'right' })
}

async function captureElement(el: HTMLElement): Promise<HTMLCanvasElement> {
  return html2canvas(el, {
    scale: 2,
    backgroundColor: '#ffffff',
    logging: false,
    useCORS: true,
  })
}

function addCanvasToPage(pdf: jsPDF, canvas: HTMLCanvasElement, y: number, maxWidth: number): number {
  const ratio = canvas.width / canvas.height
  const w = Math.min(maxWidth, CONTENT_W)
  const h = w / ratio
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', MARGIN, y, w, h)
  return h
}

function drawCoverPage(pdf: jsPDF, title: string, stats: AdminSurveyStats) {
  drawAccentBar(pdf)

  pdf.setFontSize(24)
  pdf.setTextColor(30, 27, 75)
  pdf.text(title, MARGIN, 80)

  pdf.setFontSize(11)
  pdf.setTextColor(100, 116, 139)
  pdf.text(`Generated on ${today()}`, MARGIN, 100)

  const metrics = [
    { label: 'Total Responses', value: String(stats.totalResponses) },
    { label: 'Completed', value: String(stats.completedResponses) },
    { label: 'Completion Rate', value: `${stats.completionRate}%` },
    { label: 'Avg. Completion Time', value: stats.averageCompletionTimeMs ? formatDuration(stats.averageCompletionTimeMs) : '---' },
  ]

  const cardW = (CONTENT_W - 16) / 2
  const cardH = 60
  const startY = 130

  metrics.forEach((m, i) => {
    const col = i % 2
    const row = Math.floor(i / 2)
    const x = MARGIN + col * (cardW + 16)
    const y = startY + row * (cardH + 12)

    pdf.setFillColor(245, 243, 255)
    pdf.roundedRect(x, y, cardW, cardH, 6, 6, 'F')

    pdf.setFontSize(9)
    pdf.setTextColor(100, 116, 139)
    pdf.text(m.label.toUpperCase(), x + 12, y + 20)

    pdf.setFontSize(22)
    pdf.setTextColor(30, 27, 75)
    pdf.text(m.value, x + 12, y + 46)
  })

  drawFooter(pdf, title, 1)
}

async function captureCharts(): Promise<{ areaCanvas: HTMLCanvasElement | null; row2Canvas: HTMLCanvasElement | null }> {
  const areaEl = document.querySelector('.chart-card-full') as HTMLElement | null
  const row2El = document.querySelector('.charts-row-2') as HTMLElement | null

  const [areaCanvas, row2Canvas] = await Promise.all([
    areaEl ? captureElement(areaEl) : Promise.resolve(null),
    row2El ? captureElement(row2El) : Promise.resolve(null),
  ])

  return { areaCanvas, row2Canvas }
}

function drawChartsPage(pdf: jsPDF, title: string, areaCanvas: HTMLCanvasElement | null, row2Canvas: HTMLCanvasElement | null, pageNum: number) {
  pdf.addPage()
  drawAccentBar(pdf)

  pdf.setFontSize(16)
  pdf.setTextColor(30, 27, 75)
  pdf.text('Charts & Trends', MARGIN, 60)

  let y = 80

  if (areaCanvas) {
    const h = addCanvasToPage(pdf, areaCanvas, y, CONTENT_W)
    y += h + 16
  }

  if (row2Canvas) {
    if (y + 200 > PAGE_H - 60) {
      drawFooter(pdf, title, pageNum)
      pdf.addPage()
      drawAccentBar(pdf)
      y = 50
      pageNum++
    }
    addCanvasToPage(pdf, row2Canvas, y, CONTENT_W)
  }

  drawFooter(pdf, title, pageNum)
  return pageNum
}

function drawQuestionPages(
  pdf: jsPDF,
  title: string,
  breakdowns: QuestionBreakdown[],
  questionLabels: Map<string, string>,
  pageNum: number,
): number {
  pdf.addPage()
  drawAccentBar(pdf)
  pageNum++

  pdf.setFontSize(16)
  pdf.setTextColor(30, 27, 75)
  pdf.text('Question Analytics', MARGIN, 60)

  let y = 80

  for (const b of breakdowns) {
    const label = questionLabels.get(b.questionId) || b.questionId
    const needsHeight = 30 + Object.keys(b.distribution).length * 18

    if (y + needsHeight > PAGE_H - 60) {
      drawFooter(pdf, title, pageNum)
      pdf.addPage()
      drawAccentBar(pdf)
      pageNum++
      y = 50
    }

    // Question title
    pdf.setFontSize(11)
    pdf.setTextColor(30, 27, 75)
    pdf.text(label, MARGIN, y)

    pdf.setFontSize(8)
    pdf.setTextColor(100, 116, 139)
    pdf.text(`${b.totalAnswers} responses  |  ${b.type.toUpperCase()}`, MARGIN, y + 12)

    if (b.type === 'rating' && b.rating.average !== null) {
      pdf.text(`Avg: ${b.rating.average}  Min: ${b.rating.min}  Max: ${b.rating.max}`, MARGIN + 200, y + 12)
    }

    y += 22

    if (b.type === 'text') {
      pdf.setFontSize(9)
      pdf.setTextColor(100, 116, 139)
      pdf.text(`${b.textResponseCount} text responses`, MARGIN + 8, y)
      y += 16
    } else {
      const sorted = Object.entries(b.distribution).sort(([, a], [, b]) => b - a)
      const max = Math.max(...sorted.map(([, c]) => c), 1)
      const barMaxW = CONTENT_W - 160

      for (const [optionName, count] of sorted) {
        const pct = Math.round((count / b.totalAnswers) * 100)
        const barW = (count / max) * barMaxW

        // Label
        pdf.setFontSize(9)
        pdf.setTextColor(55, 65, 81)
        const truncated = optionName.length > 20 ? optionName.slice(0, 20) + '...' : optionName
        pdf.text(truncated, MARGIN + 8, y + 4)

        // Bar
        pdf.setFillColor(168, 85, 247)
        pdf.roundedRect(MARGIN + 110, y - 4, Math.max(barW, 2), 12, 3, 3, 'F')

        // Count
        pdf.setFontSize(8)
        pdf.setTextColor(100, 116, 139)
        pdf.text(`${count} (${pct}%)`, MARGIN + 120 + barMaxW, y + 4)

        y += 18
      }
    }

    y += 10
  }

  drawFooter(pdf, title, pageNum)
  return pageNum
}

function drawRespondentPages(
  pdf: jsPDF,
  title: string,
  results: AdminResultItem[],
  questionLabels: Map<string, string>,
  pageNum: number,
): number {
  pdf.addPage()
  drawAccentBar(pdf)
  pageNum++

  pdf.setFontSize(16)
  pdf.setTextColor(30, 27, 75)
  pdf.text('Respondent Responses', MARGIN, 60)

  let y = 80

  // Table header
  pdf.setFillColor(245, 243, 255)
  pdf.rect(MARGIN, y - 10, CONTENT_W, 18, 'F')
  pdf.setFontSize(8)
  pdf.setTextColor(100, 116, 139)
  pdf.text('NAME', MARGIN + 4, y)
  pdf.text('EMAIL', MARGIN + 120, y)
  pdf.text('DATE', MARGIN + 280, y)
  pdf.text('STATUS', MARGIN + 360, y)
  pdf.text('DURATION', MARGIN + 420, y)
  y += 16

  for (const r of results) {
    const answerEntries = r.data ? Object.entries(r.data) : []
    const rowHeight = 16 + answerEntries.length * 13

    if (y + rowHeight > PAGE_H - 60) {
      drawFooter(pdf, title, pageNum)
      pdf.addPage()
      drawAccentBar(pdf)
      pageNum++
      y = 50

      // Repeat header
      pdf.setFillColor(245, 243, 255)
      pdf.rect(MARGIN, y - 10, CONTENT_W, 18, 'F')
      pdf.setFontSize(8)
      pdf.setTextColor(100, 116, 139)
      pdf.text('NAME', MARGIN + 4, y)
      pdf.text('EMAIL', MARGIN + 120, y)
      pdf.text('DATE', MARGIN + 280, y)
      pdf.text('STATUS', MARGIN + 360, y)
      pdf.text('DURATION', MARGIN + 420, y)
      y += 16
    }

    // Row
    pdf.setFontSize(9)
    pdf.setTextColor(30, 27, 75)
    pdf.text(r.respondentName || 'Anonymous', MARGIN + 4, y)
    pdf.setTextColor(100, 116, 139)
    pdf.text(r.respondentEmail || '---', MARGIN + 120, y)
    pdf.text(r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '---', MARGIN + 280, y)

    if (r.completed) {
      pdf.setTextColor(34, 197, 94)
      pdf.text('Completed', MARGIN + 360, y)
    } else {
      pdf.setTextColor(239, 68, 68)
      pdf.text('Incomplete', MARGIN + 360, y)
    }

    pdf.setTextColor(100, 116, 139)
    pdf.text(r.durationMs ? formatDuration(r.durationMs) : '---', MARGIN + 420, y)
    y += 14

    // Answers
    if (answerEntries.length > 0) {
      for (const [qId, value] of answerEntries) {
        const qLabel = questionLabels.get(qId) || qId
        const answer = Array.isArray(value) ? value.join(', ') : String(value ?? '---')
        const truncAnswer = answer.length > 60 ? answer.slice(0, 60) + '...' : answer

        pdf.setFontSize(8)
        pdf.setTextColor(124, 58, 237)
        pdf.text(qLabel.slice(0, 25), MARGIN + 16, y)
        pdf.setTextColor(55, 65, 81)
        pdf.text(truncAnswer, MARGIN + 150, y)
        y += 13
      }
    }

    // Divider line
    pdf.setDrawColor(229, 231, 235)
    pdf.line(MARGIN, y, PAGE_W - MARGIN, y)
    y += 8
  }

  drawFooter(pdf, title, pageNum)
  return pageNum
}

export interface ExportPdfOptions {
  stats: AdminSurveyStats
  results: AdminResultItem[]
  surveyTitle: string
  surveyId: string
  questionLabels: Map<string, string>
}

export async function exportSummaryPdf(opts: ExportPdfOptions): Promise<void> {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' })
  const { stats, surveyTitle } = opts

  // Page 1: Cover + Metrics
  drawCoverPage(pdf, surveyTitle, stats)

  // Page 2: Charts
  const { areaCanvas, row2Canvas } = await captureCharts()
  drawChartsPage(pdf, surveyTitle, areaCanvas, row2Canvas, 2)

  const dateStr = new Date().toISOString().slice(0, 10)
  pdf.save(`survey-${opts.surveyId}-summary-${dateStr}.pdf`)
}

export async function exportFullReportPdf(opts: ExportPdfOptions): Promise<void> {
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' })
  const { stats, results, surveyTitle, questionLabels } = opts
  const breakdowns = stats.questionBreakdowns || []

  // Page 1: Cover + Metrics
  drawCoverPage(pdf, surveyTitle, stats)

  // Page 2: Charts
  const { areaCanvas, row2Canvas } = await captureCharts()
  let pageNum = drawChartsPage(pdf, surveyTitle, areaCanvas, row2Canvas, 2)

  // Pages 3+: Question Analytics
  if (breakdowns.length > 0) {
    pageNum = drawQuestionPages(pdf, surveyTitle, breakdowns, questionLabels, pageNum)
  }

  // Final pages: Respondent Table
  if (results.length > 0) {
    drawRespondentPages(pdf, surveyTitle, results, questionLabels, pageNum)
  }

  const dateStr = new Date().toISOString().slice(0, 10)
  pdf.save(`survey-${opts.surveyId}-report-${dateStr}.pdf`)
}
